import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { PriorityEnum } from '@application/enums/priority-enum';
import { TaskCompletionStatusEnum } from '@application/enums/task-completion-status-enum';
import { getTasksGroupedByStatusAsync } from '@application/features/task/taskThunk';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { formatTaskDate } from '@application/utilities/date-helper';
import { alpha, Box, Button, Typography, useMediaQuery, useTheme, Chip, Tabs, Tab } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid2';
import TaskCard from '@ui/components/Cards/TaskCard';
import Heading from '@ui/components/Heading';
import HeadingBar from '@ui/components/HeadingBar';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import AddNewTask from './AddNewTask';
import AddTaskDrawer from './AddNewTask/addNewTaskDrawer';
import useTasksStyles from './useTasksStyles';
import { useSelector } from 'react-redux';
import { hasPermission } from '@application/helpers/permission-helper';
import { PageLayout } from '@ui/components/PageLayout';
import { useHelpTopic } from '@ui/contexts';
import { Task } from 'iconsax-react';
import AddNewTaskDialog from './AddNewTask/addNewTaskDialog';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { HFilledTextField } from '@ui/components/common/TextField';
import BDrawer from '@ui/components/common/BDrawer';
import CustomDialog from '@ui/components/common/Dialog';
import { getAllVDKEventsForTaskAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { TaskType } from '@application/enums/task-type';
import { centeredRowStack } from '@ui/style/style';
import { getAllPeopleForTasksAsync } from '@application/features/people/peopleThunk';
import { getAllUsersWithoutPagination } from '@application/features/user/userThunk';
import { getAllCollaboratorsAsync } from '@application/features/collaborator/collaboratorThunk';
import { useForm, Controller } from 'react-hook-form';
import { CustomDatePicker } from '@ui/components/common/DateTimePicker';
const TASKS_TAB_TOPICS = ['new', 'inProgress', 'completed'];
const TabPanel = ({ children, value, index }) => {
    if (value !== index)
        return null;
    return (_jsx(Box, { role: 'tabpanel', hidden: value !== index, id: `tabpanel-${index}`, children: value === index && _jsx(Box, { children: children }) }));
};
export const users = [
    'Alice Johnson',
    'Bob Smith',
    'Charlie Williams',
    'Diana Brown',
    'Edward Davis',
    'Fiona Garcia',
    'George Martinez',
    'Hannah Lee',
    'Isaac Wilson',
    'Jenna Taylor'
];
const Tasks = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const styles = useTasksStyles();
    const dispatch = useAppDispatch();
    const selectedCompanyId = useSelector((state) => state.company.selectedCompanyId);
    // Get taskId from URL query parameters
    const [searchParams] = useSearchParams();
    const highlightedTaskId = searchParams.get('taskId');
    const highlightedTaskRef = useRef(null);
    const [searchValue, setSearchValue] = useState('');
    const [filters, setFilters] = useState({});
    const [tempFilters, setTempFilters] = useState({});
    const [openFilterDialog, setOpenFilterDialog] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const tabLabels = [t('tasks.new'), t('tasks.assigned'), t('tasks.complete')];
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    // The New/In Progress/Completed Tabs UI only exists on small screens — on
    // desktop all three render at once as columns, so there's no single active
    // tab to key help content off; fall back to the general board content there.
    useHelpTopic(isSmallScreen ? TASKS_TAB_TOPICS[activeTab] : null);
    const { tasksByStatus } = useAppSelector((state) => state.task);
    const vdkEventState = useAppSelector((state) => state.VDKEvent);
    const peopleState = useAppSelector((state) => state.people);
    const userState = useAppSelector((state) => state.user);
    const collaboratorState = useAppSelector((state) => state.collaborator);
    // Create a mapping to track person relationships (moved here before buildQueryWithFilters)
    const personRelationshipMap = (() => {
        const people = peopleState.peopleForTasks || [];
        const map = {};
        people.forEach((person) => {
            // Map the main person
            map[person.id] = { isFamilyMember: false, parentPersonId: person.id };
            // Map each family member
            if (person.familyMembers && person.familyMembers.length > 0) {
                person.familyMembers.forEach((family) => {
                    map[family.id] = {
                        isFamilyMember: true,
                        parentPersonId: person.id,
                        relationId: family.id
                    };
                });
            }
        });
        return map;
    })();
    const buildQueryWithFilters = () => {
        const selectedPersonId = filters.personId;
        const relationship = selectedPersonId ? personRelationshipMap[selectedPersonId] : null;
        const mappedPersonId = relationship ? (relationship.parentPersonId || null) : null;
        const mappedRelationId = relationship ? (relationship.relationId || null) : null;
        return {
            searchString: searchValue || null,
            taskName: filters.taskName || null,
            taskDescription: filters.taskDescription || null,
            eventId: filters.eventId || null,
            taskType: filters.taskType !== undefined && filters.taskType !== null ? filters.taskType : null,
            personId: mappedPersonId,
            relationId: mappedRelationId,
            assigneeId: filters.assigneeId || null,
            priority: filters.priority !== undefined && filters.priority !== null ? filters.priority : null,
            collaboratorId: filters.collaboratorId || null,
            deadlineDateFrom: filters.deadlineDateFrom || null,
            deadlineDateTo: filters.deadlineDateTo || null
        };
    };
    const handleTaskCreated = () => {
        dispatch(getTasksGroupedByStatusAsync(buildQueryWithFilters()));
    };
    // Fetch tasks grouped by status whenever filters or search value change
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0) {
            dispatch(getTasksGroupedByStatusAsync(buildQueryWithFilters()));
        }
    }, [selectedCompanyId, dispatch, filters, searchValue]);
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0) {
            dispatch(getAllVDKEventsForTaskAsync());
            dispatch(getAllPeopleForTasksAsync());
            dispatch(getAllUsersWithoutPagination());
            dispatch(getAllCollaboratorsAsync({ pageNumber: 1, pageSize: 1000 }));
        }
    }, [selectedCompanyId, dispatch]);
    const handleTaskStatusUpdate = () => {
        const selectedPersonId = filters.personId;
        const relationship = selectedPersonId ? personRelationshipMap[selectedPersonId] : null;
        const mappedPersonId = relationship ? (relationship.parentPersonId || null) : null;
        const mappedRelationId = relationship ? (relationship.relationId || null) : null;
        dispatch(getTasksGroupedByStatusAsync({
            searchString: searchValue || null,
            taskName: filters.taskName || null,
            taskDescription: filters.taskDescription || null,
            eventId: filters.eventId || null,
            taskType: filters.taskType !== undefined && filters.taskType !== null ? filters.taskType : null,
            personId: mappedPersonId,
            relationId: mappedRelationId,
            assigneeId: filters.assigneeId || null,
            priority: filters.priority !== undefined && filters.priority !== null ? filters.priority : null,
            collaboratorId: filters.collaboratorId || null,
            deadlineDateFrom: filters.deadlineDateFrom || null,
            deadlineDateTo: filters.deadlineDateTo || null
        }));
    };
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canReadTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Read');
    const canCreateTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Create');
    const canUpdateTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Update');
    const canDeleteTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Delete');
    const userRoles = useSelector((state) => state.auth.currentUser?.role);
    const isCollaborator = Array.isArray(userRoles) && (userRoles.includes('Collaborator') || userRoles.includes('TransportManager'));
    // Group tasks by completion status
    const newTasks = tasksByStatus?.find((group) => group.completionStatus == TaskCompletionStatusEnum.New)?.tasks || [];
    const inProgressTasks = tasksByStatus?.find((group) => group.completionStatus == TaskCompletionStatusEnum.InProgress)?.tasks || [];
    const completedTasks = tasksByStatus?.find((group) => group.completionStatus == TaskCompletionStatusEnum.Completed)?.tasks || [];
    // Auto-switch to the correct tab for the highlighted task (mobile view)
    useEffect(() => {
        if (highlightedTaskId && tasksByStatus) {
            // Find which status group contains the highlighted task
            const taskInNew = newTasks.some((task) => task.id === highlightedTaskId);
            const taskInProgress = inProgressTasks.some((task) => task.id === highlightedTaskId);
            const taskInCompleted = completedTasks.some((task) => task.id === highlightedTaskId);
            if (taskInNew && activeTab !== 0) {
                setActiveTab(0);
            }
            else if (taskInProgress && activeTab !== 1) {
                setActiveTab(1);
            }
            else if (taskInCompleted && activeTab !== 2) {
                setActiveTab(2);
            }
        }
    }, [highlightedTaskId, tasksByStatus, newTasks, inProgressTasks, completedTasks, activeTab]);
    // Auto-scroll to highlighted task when available
    useEffect(() => {
        if (highlightedTaskId && highlightedTaskRef.current) {
            const timer = setTimeout(() => {
                highlightedTaskRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }, 300); // Small delay to ensure DOM is ready
            return () => clearTimeout(timer);
        }
    }, [highlightedTaskId, tasksByStatus, activeTab]);
    // Helper function to map priority enum to TaskCard priority
    const mapPriorityToTaskCard = (priority) => {
        if (priority === undefined || priority === null) {
            return PriorityEnum.Medium;
        }
        switch (priority) {
            // case PriorityEnum.Critical:
            case PriorityEnum.High:
                return PriorityEnum.High;
            case PriorityEnum.Medium:
                return PriorityEnum.Medium;
            case PriorityEnum.Low:
                // case PriorityEnum.Trivial:
                return PriorityEnum.Low;
            case PriorityEnum.None:
            default:
                return PriorityEnum.Medium; // Default to medium for None or any unhandled cases
        }
    };
    // Helper function to create status chips that match HeadingBar colors
    const createStatusChip = (count, status) => {
        let chipStyle = {};
        switch (status) {
            case TaskCompletionStatusEnum.New:
                chipStyle = {
                    backgroundColor: theme.palette.grey[300],
                    color: theme.palette.grey[800]
                };
                break;
            case TaskCompletionStatusEnum.InProgress:
                chipStyle = {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText
                };
                break;
            case TaskCompletionStatusEnum.Completed:
                chipStyle = {
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.success.contrastText
                };
                break;
            default:
                chipStyle = {
                    backgroundColor: '#F5F5F5',
                    color: '#F5F5F5'
                };
        }
        return (_jsx(Chip, { label: count.toString(), size: 'small', sx: {
                ...chipStyle,
                borderRadius: '500px',
                height: '20px',
                fontWeight: 400
            } }));
    };
    const priorityOptions = [
        { label: t('priority.high') || 'Most important', value: PriorityEnum.High },
        { label: t('priority.medium') || 'Medium', value: PriorityEnum.Medium },
        { label: t('priority.low') || 'Low', value: PriorityEnum.Low }
    ];
    const taskTypeOptions = [
        { label: t('tasks.event') || 'Event', value: TaskType.Event },
        { label: t('tasks.peopleActivity') || 'People activity', value: TaskType.People_Activity },
        { label: t('tasks.others') || 'Others', value: TaskType.Others }
    ];
    const eventOptions = (vdkEventState.VDKEventForTasks || []).map((event) => ({
        label: event.eventName,
        value: event.eventId
    }));
    // Convert people to dropdown options with family member relationships
    const peopleOptions = (() => {
        const people = peopleState.peopleForTasks || [];
        const options = [];
        people.forEach((person) => {
            // Add the main person as an option
            options.push({
                label: person.name,
                value: person.id
            });
            // Add each family member as a separate option
            if (person.familyMembers && person.familyMembers.length > 0) {
                person.familyMembers.forEach((family) => {
                    options.push({
                        label: `${family.name} (${family.relation} of ${person.name})`,
                        value: family.id
                    });
                });
            }
        });
        return options;
    })();
    const assigneeOptions = (userState.usersWithoutPagination || []).map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id
    }));
    const collaboratorOptions = (collaboratorState.collaborators || []).map((c) => ({
        label: `${c.firstName} ${c.lastName}`,
        value: c.id
    }));
    // Initializing a React Hook Form context for the filters dialog
    const { control, handleSubmit, watch, setValue: setFormValue, reset } = useForm({
        defaultValues: {
            taskName: '',
            priority: null,
            taskType: null,
            eventId: null,
            personId: null,
            assigneeId: null,
            collaboratorId: null,
            deadlineDateFrom: null,
            deadlineDateTo: null
        }
    });
    const watchedTaskType = watch('taskType');
    // Sync tempFilters when filter dialog opens
    useEffect(() => {
        if (openFilterDialog) {
            reset({
                taskName: tempFilters.taskName ?? '',
                priority: tempFilters.priority ?? null,
                taskType: tempFilters.taskType ?? null,
                eventId: tempFilters.eventId ?? null,
                personId: tempFilters.personId ?? null,
                assigneeId: tempFilters.assigneeId ?? null,
                collaboratorId: tempFilters.collaboratorId ?? null,
                deadlineDateFrom: tempFilters.deadlineDateFrom ? new Date(tempFilters.deadlineDateFrom) : null,
                deadlineDateTo: tempFilters.deadlineDateTo ? new Date(tempFilters.deadlineDateTo) : null
            });
        }
    }, [openFilterDialog, tempFilters, reset]);
    const renderAllFilters = () => {
        const makeAutocomplete = (options, field, label) => (_jsx(Controller, { name: field, control: control, render: ({ field: { value, onChange } }) => {
                const selectedOption = options.find((opt) => opt.value === value) || null;
                return (_jsx(Box, { children: _jsx(Autocomplete, { options: options, value: selectedOption, onChange: (_, newValue) => {
                            onChange(newValue?.value !== undefined ? newValue.value : null);
                            // When taskType changes, clear dependent eventId/personId values
                            if (field === 'taskType') {
                                setFormValue('eventId', null);
                                setFormValue('personId', null);
                            }
                        }, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, val) => option.value === val.value, renderInput: (params) => (_jsx(HFilledTextField, { ...params, label: label, fullWidth: true, bg: 'grey8' })), fullWidth: true }) }));
            } }));
        return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, padding: 2, children: [_jsx(Box, { children: _jsx(Controller, { name: 'taskName', control: control, render: ({ field }) => (_jsx(HFilledTextField, { ...field, label: t('tasks.taskName') || 'Task name', fullWidth: true, bg: 'grey8' })) }) }), makeAutocomplete(priorityOptions, 'priority', t('tasks.priority') || 'Priority'), makeAutocomplete(taskTypeOptions, 'taskType', t('tasks.taskType') || 'Task type'), makeAutocomplete(eventOptions, 'eventId', t('tasks.event') || 'Event'), makeAutocomplete(peopleOptions, 'personId', t('tasks.people') || 'People'), makeAutocomplete(assigneeOptions, 'assigneeId', t('tasks.assignTo') || 'Assign to'), makeAutocomplete(collaboratorOptions, 'collaboratorId', t('tasks.collaborators') || 'Supplier'), _jsx(Box, { children: _jsx(CustomDatePicker, { control: control, name: 'deadlineDateFrom', bg: 'grey8', label: t('labels.deadlinefrom') }) }), _jsx(Box, { children: _jsx(CustomDatePicker, { control: control, name: 'deadlineDateTo', bg: 'grey8', label: t('labels.deadlineto') }) })] }));
    };
    if (!canReadTasks) {
        return (_jsxs(Box, { sx: { ...styles.main }, children: [_jsx(Heading, { children: t('common.tasks') }), _jsx(Typography, { color: 'error', sx: { mt: 4 }, children: t('common.noPermissions') })] }));
    }
    return (_jsxs(PageLayout, { title: t('common.tasks'), secondTitle: t('tasks.taskManagerSubtitle'), showLeftIcon: true, leftIcon: _jsx(Task, {}), rightContent: _jsx(Button, { variant: 'contained', color: 'primary', size: 'large', startIcon: _jsx(Task, { size: 16 }), disabled: !canCreateTasks || isCollaborator, onClick: () => {
                if (isSmallScreen) {
                    setDrawerOpen(true);
                }
                else {
                    setDialogOpen(true);
                }
            }, children: t('actions.newTask') }), showTabs: isSmallScreen, tabs: _jsxs(Tabs, { value: activeTab, onChange: (_, newValue) => setActiveTab(newValue), variant: 'scrollable', children: [_jsx(Tab, { label: t('tasks.new'), value: 0 }), _jsx(Tab, { label: t('tasks.inProgress'), value: 1 }), _jsx(Tab, { label: t('tasks.completed'), value: 2 })] }), children: [_jsx(Box, { display: 'flex', justifyContent: 'flex-end', sx: { mb: 2 }, children: _jsx(SearchFilter, { searchValue: searchValue, onSearchChange: (value) => setSearchValue(value), placeholder: t('tasks.search') || 'Search...', onFilterClick: () => {
                        setTempFilters(filters);
                        setOpenFilterDialog(true);
                    }, filterLabel: t('events.filters') || 'Filters', breakpoint: 'lg', onClear: () => {
                        setSearchValue('');
                        setFilters({});
                        setTempFilters({});
                    }, showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                        setFilters({});
                        setTempFilters({});
                    } }) }), isSmallScreen ? (_jsxs(_Fragment, { children: [_jsx(TabPanel, { value: activeTab, index: 0, children: _jsx(Box, { sx: { ...styles.tasksContainer }, children: newTasks.map((task) => {
                                const taskDate = formatTaskDate(task.deadline);
                                const isHighlighted = highlightedTaskId === task.id;
                                return (_jsx(Box, { ref: isHighlighted ? highlightedTaskRef : null, children: _jsx(TaskCard, { heading: task.name, taskType: task.taskType, personId: task.personId, relationId: task.relationId, eventOrganizer: task.eventName || 'N/A', date: taskDate.date, time: taskDate.time, priority: mapPriorityToTaskCard(task.priority), status: TaskCompletionStatusEnum.New, taskData: task, onStatusUpdate: handleTaskStatusUpdate, highlighted: isHighlighted }) }, task.id));
                            }) }) }), _jsx(TabPanel, { value: activeTab, index: 1, children: _jsx(Box, { sx: { ...styles.tasksContainer }, children: inProgressTasks.map((task) => {
                                const taskDate = formatTaskDate(task.deadline);
                                const isHighlighted = highlightedTaskId === task.id;
                                return (_jsx(Box, { ref: isHighlighted ? highlightedTaskRef : null, children: _jsx(TaskCard, { heading: task.name, taskType: task.taskType, personId: task.personId, relationId: task.relationId, eventOrganizer: task.eventName || 'N/A', date: taskDate.date, time: taskDate.time, priority: mapPriorityToTaskCard(task.priority), status: TaskCompletionStatusEnum.InProgress, taskData: task, onStatusUpdate: handleTaskStatusUpdate, highlighted: isHighlighted }) }, task.id));
                            }) }) }), _jsx(TabPanel, { value: activeTab, index: 2, children: _jsx(Box, { sx: { ...styles.tasksContainer }, children: completedTasks.map((task) => {
                                const taskDate = formatTaskDate(task.deadline);
                                const isHighlighted = highlightedTaskId === task.id;
                                return (_jsx(Box, { ref: isHighlighted ? highlightedTaskRef : null, children: _jsx(TaskCard, { heading: task.name, taskType: task.taskType, personId: task.personId, relationId: task.relationId, eventOrganizer: task.eventName || 'N/A', date: taskDate.date, time: taskDate.time, priority: mapPriorityToTaskCard(task.priority), status: TaskCompletionStatusEnum.Completed, taskData: task, onStatusUpdate: handleTaskStatusUpdate, highlighted: isHighlighted }) }, task.id));
                            }) }) }), _jsx(AddTaskDrawer, { isCreateAllowed: canCreateTasks && !isCollaborator, drawerOpen: drawerOpen, setDrawerClose: () => setDrawerOpen(false) })] })) : (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                            position: 'sticky',
                            top: '0px',
                            pt: 1,
                            left: 20,
                            borderTopLeftRadius: 6,
                            zIndex: 1001,
                            background: '#FFFFFF'
                        }, children: _jsxs(Grid, { container: true, spacing: 1.5, columnGap: 1.5, children: [_jsx(Grid, { size: { xs: 12, md: 6, lg: 4 }, children: _jsx(HeadingBar, { type: TaskCompletionStatusEnum.New, noOfTasks: newTasks.length, title: t('tasks.new') }) }), _jsx(Grid, { size: { xs: 12, md: 6, lg: 4 }, children: _jsx(HeadingBar, { type: TaskCompletionStatusEnum.InProgress, noOfTasks: inProgressTasks.length, title: t('tasks.inProgress') }) }), _jsx(Grid, { size: { xs: 12, md: 6, lg: 4 }, children: _jsx(HeadingBar, { type: TaskCompletionStatusEnum.Completed, bgcolor: alpha(theme.palette.success.main, 0.08), noOfTasks: completedTasks.length, title: t('tasks.completed') }) })] }) }), _jsxs(Grid, { container: true, spacing: 1.5, columnGap: 1.5, children: [_jsx(Grid, { size: { xs: 12, md: 6, lg: 4 }, children: _jsx(Box, { sx: { ...styles.tasksContainer }, children: newTasks.map((task) => {
                                        const taskDate = formatTaskDate(task.deadline);
                                        const isHighlighted = highlightedTaskId === task.id;
                                        return (_jsx(Box, { ref: isHighlighted ? highlightedTaskRef : null, children: _jsx(TaskCard, { heading: task.name, taskType: task.taskType, personId: task.personId, relationId: task.relationId, eventOrganizer: task.eventName || 'N/A', date: taskDate.date, time: taskDate.time, priority: mapPriorityToTaskCard(task.priority), status: TaskCompletionStatusEnum.New, taskData: task, onStatusUpdate: handleTaskStatusUpdate, highlighted: isHighlighted }) }, task.id));
                                    }) }) }), _jsx(Grid, { size: { xs: 12, md: 6, lg: 4 }, children: _jsx(Box, { sx: { ...styles.tasksContainer }, children: inProgressTasks.map((task) => {
                                        const taskDate = formatTaskDate(task.deadline);
                                        const isHighlighted = highlightedTaskId === task.id;
                                        return (_jsx(Box, { ref: isHighlighted ? highlightedTaskRef : null, children: _jsx(TaskCard, { heading: task.name, taskType: task.taskType, personId: task.personId, relationId: task.relationId, eventOrganizer: task.eventName || 'N/A', date: taskDate.date, time: taskDate.time, priority: mapPriorityToTaskCard(task.priority), status: TaskCompletionStatusEnum.InProgress, taskData: task, onStatusUpdate: handleTaskStatusUpdate, highlighted: isHighlighted }) }, task.id));
                                    }) }) }), _jsx(Grid, { size: { xs: 12, md: 6, lg: 4 }, children: _jsx(Box, { sx: { ...styles.tasksContainer }, children: completedTasks.map((task) => {
                                        const taskDate = formatTaskDate(task.deadline);
                                        const isHighlighted = highlightedTaskId === task.id;
                                        return (_jsx(Box, { ref: isHighlighted ? highlightedTaskRef : null, children: _jsx(TaskCard, { heading: task.name, taskType: task.taskType, personId: task.personId, relationId: task.relationId, eventOrganizer: task.eventName || 'N/A', date: taskDate.date, time: taskDate.time, priority: mapPriorityToTaskCard(task.priority), status: TaskCompletionStatusEnum.Completed, taskData: task, onStatusUpdate: handleTaskStatusUpdate, userAssignedDisabled: true, highlighted: isHighlighted }) }, task.id));
                                    }) }) })] }), _jsx(Box, { style: { display: 'none', visibility: 'hidden' }, children: _jsx(AddNewTask, { isCreateAllowed: canCreateTasks && !isCollaborator }) }), _jsx(AddNewTaskDialog, { open: dialogOpen, onClose: () => setDialogOpen(false), isCreateAllowed: canCreateTasks && !isCollaborator, onTaskCreated: handleTaskCreated })] })), openFilterDialog &&
                (isSmallScreen ? (_jsxs(BDrawer, { anchor: 'bottom', title: t('users.filters') || 'Filters', filterLabel: t('actions.clearFilters'), open: openFilterDialog, onClose: () => {
                        setOpenFilterDialog(false);
                    }, showClearFilterButton: Object.keys(filters).length > 0, handleClearFilter: () => {
                        setFilters({});
                        setTempFilters({});
                        reset({
                            taskName: '',
                            priority: null,
                            taskType: null,
                            eventId: null,
                            personId: null,
                            assigneeId: null,
                            collaboratorId: null,
                            deadlineDateFrom: null,
                            deadlineDateTo: null
                        });
                        setOpenFilterDialog(false);
                    }, children: [renderAllFilters(), _jsxs(Box, { sx: { ...centeredRowStack, p: 2, gap: 1 }, children: [_jsx(Button, { fullWidth: true, onClick: () => {
                                        setOpenFilterDialog(false);
                                    }, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: handleSubmit((data) => {
                                        const cleanedFilters = Object.fromEntries(Object.entries(data).map(([key, value]) => {
                                            if (value instanceof Date) {
                                                return [key, value.toISOString()];
                                            }
                                            return [key, value];
                                        }).filter(([_, value]) => {
                                            return value !== null && value !== undefined && value !== '';
                                        }));
                                        setFilters(cleanedFilters);
                                        setTempFilters(cleanedFilters);
                                        setOpenFilterDialog(false);
                                    }), children: t('actions.applyFilters') })] })] })) : (_jsx(CustomDialog, { backgroundVariant: 'white', open: openFilterDialog, title: t('users.filters') || 'Filters', onClose: () => {
                        setOpenFilterDialog(false);
                    }, actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, onClick: () => {
                                    setOpenFilterDialog(false);
                                }, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: handleSubmit((data) => {
                                    const cleanedFilters = Object.fromEntries(Object.entries(data).map(([key, value]) => {
                                        if (value instanceof Date) {
                                            return [key, value.toISOString()];
                                        }
                                        return [key, value];
                                    }).filter(([_, value]) => {
                                        return value !== null && value !== undefined && value !== '';
                                    }));
                                    setFilters(cleanedFilters);
                                    setTempFilters(cleanedFilters);
                                    setOpenFilterDialog(false);
                                }), children: t('actions.applyFilters') })] }), children: renderAllFilters() })))] }));
};
export default Tasks;
